import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'sapa_favorites';

const FavoritesContext = createContext();

export const useFavorites = () => {
    const context = useContext(FavoritesContext);
    if (!context) {
        throw new Error('useFavorites must be used within a FavoritesProvider');
    }
    return context;
};

export const FavoritesProvider = ({ children }) => {
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);

    // Load favorites from localStorage on mount
    const fetchFavorites = useCallback(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                setFavorites(JSON.parse(stored));
            }
        } catch (error) {
            console.error('Error loading favorites:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFavorites();
    }, [fetchFavorites]);

    // Save to localStorage whenever favorites change
    useEffect(() => {
        if (!loading) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
        }
    }, [favorites, loading]);

    const addFavorite = (spaId) => {
        setFavorites(prev => [...prev, spaId]);
    };

    const removeFavorite = (spaId) => {
        setFavorites(prev => prev.filter(id => id !== spaId));
    };

    const toggleFavorite = (spaId) => {
        if (favorites.includes(spaId)) {
            removeFavorite(spaId);
        } else {
            addFavorite(spaId);
        }
    };

    const isFavorite = (spaId) => {
        return favorites.includes(spaId);
    };

    return (
        <FavoritesContext.Provider value={{
            favorites,
            loading,
            addFavorite,
            removeFavorite,
            toggleFavorite,
            isFavorite,
            fetchFavorites
        }}>
            {children}
        </FavoritesContext.Provider>
    );
};
